import {SkeletonRingLoader} from '@/components/common-components';
import {Button} from '@/components/common-components/v2';
import {useStore} from '@/store/root-store';
import {BlockImage, Table} from '@/utils/blockRenderComponents';
import {opaqueColor} from '@/utils/colors';
import {notification} from '@/utils/notification';
import {LoadingOutlined} from '@ant-design/icons';
import {faBrainCircuit} from '@fortawesome/pro-duotone-svg-icons';
import {faArrowRight, faTrash} from '@fortawesome/pro-regular-svg-icons';
import {faArrowsRotate, faICursor} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Spin, Tooltip} from 'antd';
import classNames from 'classnames';
import {Editor, EditorState, KeyBindingUtil, RichUtils, SelectionState, getDefaultKeyBinding,
  Modifier,
  genKey,
  ContentBlock,
} from 'draft-js';
import 'draft-js/dist/Draft.css';
import {debounce, kebabCase} from 'lodash';
import {toJS} from 'mobx';
import {observer} from 'mobx-react-lite';
import {getLocalesByKey as t} from '@/utils/translation';
import {useRouter} from 'next/router';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {CustomBlock} from './CustomBlock';
import styles from './style.module.scss';
import {Welcome} from './welcome';
import {convertHtmlToEditorState} from '@/utils/htmlToDraft';
import {customStyleMap} from '@/utils/renderConfig';
import {Map} from 'immutable';
import {getSingleUrlParam} from '@/utils/url';
import {NextImg} from '@/utils/nextImg';


const styleMap = {
  'HIGHLIGHT': {
    'backgroundColor': '#faed27',
  },
  'NEWTEXT': {
    'backgroundColor': '#ebf7fe',
  },
  'MONOSPACE': {
    'backgroundColor': 'rgba(0, 0, 0, 0.05)',
    'fontFamily': 'Inconsolata, Menlo, Consolas, monospace',
    'fontSize': '16px',
    'padding': '2px',
  },
};


export const ContentEditor = observer(
  ({onChange, showAiBanner}:
    {
      onChange: (editorState: any) => any;
      showAiBanner: boolean;
    }) => {
    const editor = useRef(null);
    const editorContainer = useRef(null);
    const heroSection = useRef(null);
    const [lastScrollPosition, setLastScrollPosition] = useState(0);


    const [addHeroImageLoading, setAddHeroImageLoading] = useState(false);
    localStorage.setItem('scaDashboardOpened', 'TRUE');

    const router = useRouter();

    const {contentOptimizer: {currentPage, aiOutline, aiMetaData, pages: {getHeroImageById}}} = useStore('');
    const {
      clearFocusTermsClusters,
      getCurrentEditorState,
      zoomLevel,
      newPage,
      clearFeTargetKeywords,
      clearTargetKeywords,
      isInitialEditorStateFetched,
      setIsEditorScrolled,
      isEditorScrolled,
      setFocusTermsSorter,
      resetRepollCounters,
      clearAllTaData,
      setCurrentEditorState,
      editContent,
      content,
      setIsUnspashDrawerOpen,
      updateHeroImage,
      isHeroImageLoading,
      setContentType,
    } = currentPage;
    const {
      clearAiOutline,
      isOpenAiOutlineDrawer,
      setIsOpenAiOutlineDrawer,
      clearV2AiOutline,
      setIsOpenAiContentToolsDrawer,
      clearTemplateOutlines,
      setIsOpenAiWriterDrawer,
      clearOneClickIdea,
      clearOneClickOutlines,
      setAiWriter,
      aiWriterController,
      setAiTemplate,
      aiTemplatesController,
    } = aiOutline;
    const {
      clearMetaData,
    } = aiMetaData;

    const {
      navBar: {
        setIsDockedSider,
      },
    } = useStore('');
    const {heroImage, aiInProgress, setHeroImage, heroImageAltText} = content;

    const antIconOrange = <LoadingOutlined style={{fontSize: 12, color: '#FF8536', marginRight: 5}} spin />;

    const myBlockRenderer = (editor, getEditorState, onChange) => contentBlock => {
      const blockTypes = ['header-one', 'header-two', 'header-three', 'header-four', 'header-five', 'header-six', 'unstyled'];
      const type = contentBlock.getType();

      if (blockTypes.includes(type)) {
        return {
          component: CustomBlock,
          editable: true,
          props: {
            editor,
            getEditorState,
            onChange,
          },
        };
      }

      if (type === 'atomic') {
        return {
          component: BlockImage,
          // editable: false,
          props: {
            editor,
            getCurrentEditorState,
            onChange,
          },
        };
      }

      if (type === 'table') {
        return {
          component: Table,
          editable: true,
          props: {
            editor,
          },
        };
      }
      // Return null for other block types
      return null;
    };

    const customBlockStyleFn = contentBlock => {
      const type = contentBlock.getType();
      if (type === 'code-block') {
        return 'codeBlock';
      } else if (type === 'header-one') {
        return 'headerOneWithTag';
      } else if (type === 'header-two') {
        return 'headerTwoWithTag';
      } else if (type === 'header-three') {
        return 'headerThreeWithTag';
      } else if (type === 'header-four') {
        return 'headerFourWithTag';
      } else if (type === 'header-five') {
        return 'headerFiveWithTag';
      } else if (type === 'header-six') {
        return 'headerSixWithTag';
      } else if (type === 'unstyled') {
        return 'paragraphWithTag';
      }
    };

    const windowHeight = window.innerHeight;

    // clean up state upon component dismount
    useEffect(() => {
      setIsDockedSider(false);
      return () => {
        clearFeTargetKeywords();
        clearFeTargetKeywords();
        clearAllTaData();
        clearTargetKeywords();
        clearFocusTermsClusters();
        setIsOpenAiContentToolsDrawer(false);
        setIsOpenAiWriterDrawer(false);
        clearMetaData();
        resetRepollCounters();
        clearAiOutline();
        clearTemplateOutlines();
        clearV2AiOutline();
        clearOneClickIdea();
        clearOneClickOutlines();
        setAiWriter({
          ...aiWriterController,
          showForm: false,
        });
        setFocusTermsSorter({
          label: 'Importance',
          key: 'normalizedScore',
        });
        setIsDockedSider(true);
        if (isOpenAiOutlineDrawer) {
          setIsOpenAiOutlineDrawer(false);
        }
        // VERY IMPORTANT NOT TO REMOVE THIS ONE BELLOW
        // It will enable top bar to reapear because we are hiding it when editor is scrolled, and we need it visible on other pages
        setIsEditorScrolled(false);
        currentPage.setAiTooltip({show: false});
      };
    }, []);

    const offsetBreakPoint = 200;

    const getZoomLevel = () => {
      switch (zoomLevel) {
        case 0.5:
          return styles.zoom05;
        case 0.75:
          return styles.zoom075;
        case 0.9:
          return styles.zoom09;
        case 1:
          return styles.zoom1;
        case 1.2:
          return styles.zoom12;
        case 1.5:
          return styles.zoom15;
        case 2:
          return styles.zoom2;
        default:
          return styles.zoom1;
      }
    };

    const removeSizeDataFromBlock = (newEditorState, block) => {
      const data = block.getData().delete('height').delete('width').delete('imgStyle');
      block = block.set('data', data);
      let contentState = newEditorState.getCurrentContent();
      let blockMap = contentState.getBlockMap();
      blockMap = blockMap.set(block.getKey(), block);
      contentState = contentState.set('blockMap', blockMap);
      newEditorState = EditorState.push(newEditorState, contentState, 'change-block-data');
      return newEditorState;
    };

    const handleTabInTable = (direction = 'next', collapsed = false) => {
      let newEditorState = getCurrentEditorState;
      let selection = getCurrentEditorState.getSelection();
      let contentState = getCurrentEditorState.getCurrentContent();
      let targetKey = selection.getAnchorKey();
      let targetBlock = contentState.getBlockForKey(targetKey);
      do {
        if (direction === 'next') {
          targetBlock = contentState.getBlockAfter(targetKey);
        } else {
          targetBlock = contentState.getBlockBefore(targetKey);
        }
        targetKey = targetBlock && targetBlock.getKey();
      } while (targetKey && ['atomic', 'horizontal-rule'].includes(targetBlock.getType()));
      if (!targetBlock && direction === 'next') {
        selection = selection.merge({
          anchorOffset: contentState.getBlockForKey(selection.getAnchorKey()).getLength(),
          focusOffset: contentState.getBlockForKey(selection.getAnchorKey()).getLength(),
        });
        contentState = Modifier.splitBlock(contentState, selection);
        targetBlock = contentState.getLastBlock();
        selection = SelectionState.createEmpty(targetBlock.getKey());
        contentState = Modifier.setBlockType(contentState, selection, 'unstyled');
        targetBlock = contentState.getLastBlock();
        newEditorState = EditorState.push(getCurrentEditorState, contentState, 'split-block');
      } else if (!targetBlock) {
        targetBlock = contentState.getBlockForKey(selection.getAnchorKey());
      }
      const isTargetTable = targetBlock.getType() === 'table' && !collapsed;
      const endOffset = targetBlock.getLength();
      selection = SelectionState.createEmpty(targetBlock.getKey());
      selection = selection.merge({
        anchorOffset: isTargetTable || direction === 'next' ? 0 : endOffset,
        focusOffset: isTargetTable || direction === 'previous' ? endOffset : 0,
      });
      onChange(EditorState.forceSelection(newEditorState, selection));
    };

    const setSelectionIfNone = currentEditorState => {
      if (!currentEditorState || typeof currentEditorState.getSelection !== 'function') {
        return EditorState.createEmpty();
      }

      const selection = currentEditorState.getSelection();
      if (!selection.getHasFocus()) {
        return moveSelectionToStart(currentEditorState);
      }
      return currentEditorState;
    };

    const toggleInlineStyle = inlineStyle => {
      let newEditorState = setSelectionIfNone(getCurrentEditorState);

      if (/^fontSize\.\d{1,2}$/.test(inlineStyle)) inlineStyle += 'pt';
      // if the new style is a compound style type (fontFamily, fontSize, color, or backgroundColor) and the current style includes the same type,
      // remove the current matching style type before turning on the new style.
      const existingMatch = newEditorState
        .getCurrentInlineStyle() // getCurrentInlineStyle() returns an Immutable OrderedSet
        .filter(style => style.includes('.') && style.split('.')[0] === inlineStyle.split('.')[0]) // compound styles are dot-delimited e.g. fontFamily.Arial
        .toList()
        .get(0);
      if (existingMatch) {
        newEditorState = RichUtils.toggleInlineStyle(newEditorState, existingMatch);
      }

      // deal with style attributes imported from html as a string
      let key = kebabCase(Object.keys(customStyleMap[inlineStyle] ?? {})[0]);
      if (!key && inlineStyle.endsWith('.unset')) key = kebabCase(inlineStyle.split('.')[0]);
      newEditorState
        .getCurrentInlineStyle()
        .filter(style => style.includes(':'))
        .filter(style => key && style.includes(key))
        .forEach(style => {
          const rexp = new RegExp(`${key}:\\s+(.*?;+\\s+|.*$)`);
          const newString = style.replace(rexp, '');
          newEditorState = RichUtils.toggleInlineStyle(newEditorState, style); // remove the full style string
          newEditorState = RichUtils.toggleInlineStyle(newEditorState, newString); // add back the modified string with the conflicting style removed
        });

      if (inlineStyle.endsWith('unset')) {
        onChange(newEditorState);
      } else {
        onChange(RichUtils.toggleInlineStyle(newEditorState, inlineStyle));
      }
    };

    const toggleListType = listType => {
      if (RichUtils.getCurrentBlockType(getCurrentEditorState) === 'table') {
        return null;
      }
      const blockType = listType === 'BULLETLIST' ? 'unordered-list-item' : 'ordered-list-item';
      onChange(RichUtils.toggleBlockType(getCurrentEditorState, blockType));
    };

    const handleDeleteKey = () => {
      const selection = getCurrentEditorState.getSelection();
      const contentState = getCurrentEditorState.getCurrentContent();
      const startBlock = contentState.getBlockForKey(selection.getStartKey());
      const endBlock = contentState.getBlockForKey(selection.getEndKey());
      const nextBlock = contentState.getBlockAfter(endBlock.getKey());
      const endOffset = selection.getEndOffset();
      // prevent deleting entire table cell
      if (selection?.isCollapsed()) {
        const length = startBlock?.getLength();
        if (endOffset === length && nextBlock?.getType() === 'table') {
          return 'handled';
        } else {
          return 'not-handled';
        }
      } else {
        return handleKeypressWhenSelectionNotCollapsed();
      }
    };

    const toggleBlockData = newData => {
      const contentState = getCurrentEditorState.getCurrentContent();
      const selectionState = getCurrentEditorState.getSelection();
      const block = contentState.getBlockForKey(selectionState.getAnchorKey());
      let data = block.getData();
      Object.keys(newData).forEach(key => {
        if (data.get(key) === newData[key]) {
          data = data.remove(key);
        } else {
          data = data.merge(newData);
        }
      });
      const newContentState = Modifier.setBlockData(contentState, selectionState, data);
      onChange(EditorState.push(getCurrentEditorState, newContentState, 'change-block-data'));
    };

    const setIndent = (
      direction,
      newEditorState = getCurrentEditorState,
      listMax = 20,
      indentMax = 20,
      setDepth,
    ) => {
      const selectionState = newEditorState.getSelection();
      const contentState = newEditorState.getCurrentContent();
      const adjustment = direction === 'INDENT' ? 1 : -1;
      const startKey = selectionState.getStartKey();
      const endKey = selectionState.getEndKey();
      let blockMap = contentState.getBlockMap();
      const blocks = blockMap
        .toSeq()
        .skipUntil((_, k) => k === startKey)
        .takeUntil((_, k) => k === endKey)
        .concat([[endKey, blockMap.get(endKey)]])
        .map(block => {
          const depth = block.getDepth();
          const maxDepth = block.getType().includes('list-item') ? listMax : indentMax;
          const newDepth = setDepth !== undefined ? setDepth : Math.max(0, Math.min(depth + adjustment, maxDepth));
          return block.set('depth', newDepth);
        });

      blockMap = blockMap.merge(blocks);
      const newContentState = contentState.merge({
        blockMap,
        selectionBefore: selectionState,
        selectionAfter: selectionState,
      });
      newEditorState = EditorState.push(newEditorState, newContentState, 'adjust-depth');
      onChange(newEditorState);
    };

    const handleKeyCommand = (command, newEditorState) => {
      switch (command) {
        case 'backspace': {
          // removing images ("atomic" blocktype) with backspace requires special handling or the image tag and dataUrl can be left in the content but not visible.
          let contentState = newEditorState?.getCurrentContent();
          let selectionState = newEditorState?.getSelection();
          const startKey = selectionState.getStartKey();
          const offset = selectionState.getStartOffset();
          const collapsed = selectionState.isCollapsed();
          const blockBefore = contentState.getBlockBefore(startKey);
          const currentBlockType = RichUtils.getCurrentBlockType(newEditorState);
          if (collapsed && offset === 0 && blockBefore && blockBefore.getType() === 'atomic') {
            newEditorState = removeSizeDataFromBlock(newEditorState, blockBefore);
            newEditorState = EditorState.acceptSelection(newEditorState, selectionState);
            onChange(RichUtils.onBackspace(newEditorState));
            return 'handled';
          } else if (currentBlockType === 'atomic') {
            const currentBlock = contentState.getBlockForKey(startKey);
            newEditorState = removeSizeDataFromBlock(newEditorState, currentBlock);
            newEditorState = EditorState.acceptSelection(newEditorState, selectionState);
            newEditorState = RichUtils.onBackspace(newEditorState);
            contentState = newEditorState?.getCurrentContent();
            selectionState = newEditorState?.getSelection();
            const key = selectionState.getAnchorKey();
            let selection = SelectionState.createEmpty(key);
            selection = selection.set('focusOffset', 1) as SelectionState;
            contentState = Modifier.removeRange(contentState, selection, 'backward');
            onChange(EditorState.push(newEditorState, contentState, 'backspace-character'));
            return 'handled';
          } else if (currentBlockType === 'table' && collapsed && offset === 0) {
            return 'handled';
          } else if (currentBlockType !== 'table' && collapsed && blockBefore?.getType() === 'table' && offset === 0) {
            handleTabInTable('previous', true);
            return 'handled';
          } else if (collapsed && offset === 0 && blockBefore?.getType() === 'page-break') {
            let selection = SelectionState.createEmpty(blockBefore.getKey());
            /* eslint-disable new-cap */
            contentState = Modifier.setBlockData(contentState, selection, Map({}));
            contentState = Modifier.setBlockType(contentState, selection, 'unstyled');
            selection = selection.merge({
              focusKey: selectionState.getFocusKey(),
              focusOffset: 0,
              hasFocus: true,
            });
            contentState = Modifier.removeRange(contentState, selection, 'backward');
            onChange(EditorState.push(newEditorState, contentState, 'remove-range'));
            return 'handled';
          } else if (currentBlockType === 'page-break') {
            /* eslint-disable new-cap */
            contentState = Modifier.setBlockData(contentState, selectionState, Map({}));
            contentState = Modifier.setBlockType(contentState, selectionState, 'unstyled');
            let selection = selectionState;
            if (collapsed && contentState.getBlockAfter(startKey)) {
              selection = selection.merge({
                focusKey: contentState.getBlockAfter(startKey).getKey(),
                focusOffset: 0,
                hasFocus: true,
              });
            }
            contentState = Modifier.removeRange(contentState, selection, 'backward');
            onChange(EditorState.push(newEditorState, contentState, 'remove-range'));
            return 'handled';
          } else if (
            collapsed &&
            offset === 0 &&
            ['pasted-list-item', 'ordered-list-item', 'unordered-list-item'].includes(currentBlockType)
          ) {
            contentState = Modifier.setBlockType(contentState, selectionState, 'unstyled');
            onChange(EditorState.push(newEditorState, contentState, 'change-block-type'));
            return 'handled';
          } else if (!collapsed) {
            return handleKeypressWhenSelectionNotCollapsed(newEditorState);
          } else {
            return 'not-handled';
          }
        }
        case 'BOLD':
          toggleInlineStyle('BOLD');
          return 'handled';
        case 'bullet_list':
          toggleListType('BULLETLIST');
          return 'handled';
        case 'delete':
          return handleDeleteKey();
        case 'float_left':
          toggleBlockData({float: 'left'});
          return 'handled';
        case 'float_right':
          toggleBlockData({float: 'right'});
          return 'handled';
        case 'INDENT':
          setIndent('INDENT', getCurrentEditorState, 20, 20, null);
          return 'handled';
        case 'ITALIC':
          toggleInlineStyle('ITALIC');
          return 'handled';
        case 'ordered_list':
          toggleListType('ORDEREDLIST');
          return 'handled';
        case 'OUTDENT':
          setIndent('OUTDENT', getCurrentEditorState, 20, 20, null);
          return 'handled';
        case 'UNDERLINE':
          toggleInlineStyle('UNDERLINE');
          return 'handled';
        case 'shiftTab':
          return 'handled';
        case 'tab':
          return 'handled';
        default:
          return 'not-handled';
      }
    };

    const keyBindingFn = event => {
      // we press CTRL + K => return 'bbbold'
      // we use hasCommandModifier instead of checking for CTRL keyCode because different OSs have different command keys
      if (KeyBindingUtil.hasCommandModifier(event) && event.keyCode === 75) {
        return 'new-link';
      }
      // manages usual things, like:
      // Ctrl+Z => return 'undo'
      return getDefaultKeyBinding(event);
    };

    useEffect(() => {
      if (editor?.current?.editor && currentPage.isEditorFocus) {
        editor.current.editor.focus();
        currentPage.setIsEditorFocus(false);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage.isEditorFocus]);

    const oneClick = getSingleUrlParam('one_click');

    useEffect(() => {
      if (oneClick) {
        writeWithAi();
      }
    }, []);

    const debouncedChange = debounce((state: EditorState, isNewDecorations = false) => {
      // we are then calling the edit content method which will fire the API call and send new state to the BE
      editContent(state, isNewDecorations);
    }, 2000);

    const debouncedUpdate = useCallback(
      (state: EditorState, isNewDecorations) => debouncedChange(state, isNewDecorations),
      // eslint-disable-next-line
      []
    );

    const onEditorBlockTypeChange = (editorState: EditorState, isNewDecorations: boolean = false, prevSelection: SelectionState) => {
      setCurrentEditorState(editorState);

      // after desired block type is changed, we are returning cursor back to last known selection
      const prevEditorState = EditorState.forceSelection(editorState, prevSelection);
      setCurrentEditorState(prevEditorState);

      // We are comparing current editor state with udpated one to make sure the flow only triggers once there is an actual content change (adding removing of words, formatting, linkjng etc.)
      debouncedUpdate(prevEditorState, isNewDecorations);
    };

    const getCurrentBlock = editorState => {
      const currentSelection = editorState.getSelection();
      const blockKey = currentSelection.getStartKey();
      return (editorState.getCurrentContent().getBlockForKey(blockKey));
    };

    const handleChangeBlockType = value => {
      const prevSelection = getCurrentEditorState.getSelection();

      const block = getCurrentBlock(getCurrentEditorState);

      if (!block) return;
      const newSelection = new SelectionState({
        anchorKey: block.getKey(), // key of block
        anchorOffset: 0,
        focusKey: block.getKey(),
        focusOffset: 0, // key of block
        hasFocus: true,
        isBackward: false, // isBackward = (focusOffset < anchorOffset)
      });
      const newEditorState = EditorState.forceSelection(getCurrentEditorState, newSelection);
      onEditorBlockTypeChange(RichUtils.toggleBlockType(newEditorState, value), true, prevSelection);
    };

    const keysMap = {
      'header-one': [17, 18, 49],
      'header-two': [17, 18, 50],
      'header-three': [17, 18, 51],
      'header-four': [17, 18, 52],
      'header-five': [17, 18, 53],
      'header-six': [17, 18, 54],
      'unstyled': [17, 18, 48],
    };

    const curPressKeys = [];

    const checkKey = e => {
      if (curPressKeys.length && curPressKeys[0] == 17 && curPressKeys[1] == 18) e.preventDefault();
      e = e || window.event;
      curPressKeys.push(e.keyCode);

      const multipleExist = (keysArray: string[]) => {
        return keysArray.every(value => {
          return curPressKeys.includes(value);
        });
      };

      Object.keys(keysMap).forEach(key => {
        if (multipleExist(keysMap[key])) {
          handleChangeBlockType(key);
        }
      });
    };

    const clearPressKeysArr = () => curPressKeys.length = 0;

    useEffect(() => {
      if (document) {
        document.addEventListener('keydown', checkKey);
        document.addEventListener('keyUp', clearPressKeysArr);

        return () => {
          document.removeEventListener('keydown', checkKey);
          document.removeEventListener('keyUp', clearPressKeysArr);
        };
      }
    });

    // document.onkeydown = checkKey;
    // document.onkeyup = () => curPressKeys.length = 0;

    const addHeroImage = () => {
      setIsUnspashDrawerOpen(true);
      setContentType('asHero');
    };

    const removeHeroImage = e => {
      e.stopPropagation();
      updateHeroImage(null, null);
    };

    const checkTitle = title => {
      const titleArray = title?.split(' ');
      if (titleArray[0] == 'New' && titleArray[1] == 'Document') {
        return false;
      } else {
        return true;
      }
    };

    const handleAddHeroImage = async () => {
      setAddHeroImageLoading(true);
      try {
        const uuid = router?.query?.uuid;
        if (content?.targetKeywordsCount > 0 || checkTitle(content?.title) || content?.cleanedHtml?.replace( /(<([^>]+)>)/ig, '') != '') {
          const res = await getHeroImageById(uuid);
          if (res) {
            setHeroImage(res, '');
            currentPage.loadPage(uuid as string, false);
          } else {
            notification.error('', 'Failed to AI Generate an image. Please try again.');
          }
        } else {
          notification.warning('You need to start writing Content or add at least one Target Keyword or add Title to get the Hero image', '');
        }
      } catch (error) {
        notification.error('', 'Failed to AI Generate an image. Please try again.');
        Promise.reject(error);
      }
      setAddHeroImageLoading(false);
    };

    useEffect(() => {
      const height = heroSection.current ? heroSection.current.offsetHeight : 250;
      if (lastScrollPosition > height) {
        editorContainer.current.scrollTo({
          top: lastScrollPosition,
        });
      }
    }, [getCurrentEditorState]);

    // used for pasting or dropping content into the editor
    const addHtmlToDocument = (newHtml, currentEditorState = getCurrentEditorState) => {
      if (!newHtml) return false;
      if (!currentEditorState.getCurrentContent().hasText()) {
        reset(newHtml);
        return true;
      } else {
        let newEditorState = convertHtmlToEditorState(newHtml);
        const newBlockMap = newEditorState.getCurrentContent().getBlockMap();

        const newContent = Modifier.replaceWithFragment(
          currentEditorState.getCurrentContent(),
          currentEditorState.getSelection(),
          newBlockMap,
        );
        newEditorState = EditorState.push(currentEditorState, newContent, 'insert-fragment');
        onChange(newEditorState);
        const currentEditor = editor.current;
        const hasFocus = getCurrentEditorState.getSelection().getHasFocus();
        setTimeout(() => {
          currentEditor?.blur();
          setTimeout(() => {
            if (hasFocus) {
              currentEditor?.focus();
            }
          });
        });
        return true;
      }
    };

    const html = useRef(null);

    const getEditorState = () => {
      return getCurrentEditorState;
    };


    const handleKeypressWhenSelectionNotCollapsed = (newEditorState = getCurrentEditorState, chars = '') => {
      let selection = newEditorState.getSelection();
      let content = newEditorState.getCurrentContent();
      const startKey = selection.getStartKey();
      const startBlock = content.getBlockForKey(startKey);
      const endKey = selection.getEndKey();
      const endBlock = content.getBlockForKey(endKey);
      const prevBlock = content.getBlockBefore(startKey);
      const nextBlock = content.getBlockAfter(endKey);
      const firstCell =
        startBlock.getType() === 'table' && selection.getStartOffset() === 0 && prevBlock?.getType() !== 'table';
      const firstTableKey = startBlock.getData().get('tableKey');
      const lastCell =
        endBlock.getType() === 'table' &&
        selection.getEndOffset() === endBlock.getLength() &&
        nextBlock?.getType() !== 'table';
      const lastTableKey = endBlock.getData().get('tableKey');
      const sameTable = firstTableKey === lastTableKey;

      if (startBlock === endBlock || (startBlock.getType() !== 'table' && endBlock.getType() !== 'table')) {
        return 'not-handled';
      }

      let blockMap = content.getBlockMap();
      const startOffset = selection.getStartOffset();
      const endOffset = selection.getEndOffset();
      let blocks = blockMap
        .toSeq()
        .skipUntil(v => v === startBlock)
        .takeUntil(v => v === nextBlock) // take up to but not including nextBlock
        .map(block => {
          let text;
          const type = block.getType();
          if (block === startBlock) {
            text = block.getText().slice(0, startOffset) + chars;
            return block.set('text', type === 'table' ? text || ' ' : text);
          }
          if (block === endBlock) {
            text = block.getText().slice(endOffset, block.getLength());
            return block.set('text', type === 'table' ? text || ' ' : text);
          }
          return block.set('text', type === 'table' ? ' ' : '');
        })
        .toOrderedMap();

      switch (true) {
        case startBlock.getType() !== 'table' && lastCell: // remove all selected blocks
        case firstCell && lastCell: // remove all selected blocks
          blockMap = blockMap.merge(blocks);
          blockMap = blockMap.filter(
            (block, key) => !blocks.has(key) || (block.getType() !== 'table' && block.getText()),
          );
          if (!blockMap.size) {
            const key = genKey();
            /* eslint-disable new-cap */
            blockMap = blockMap.merge([[key, new ContentBlock({key, type: 'unstyled', text: '', data: Map()})]]);
          }
          break;
        case firstCell && endBlock.getType() !== 'table': {
          // remove all selected blocks, but preserve inline style/entities in partial block after selection
          content = Modifier.removeRange(content, selection, 'backward');
          blockMap = content.getBlockMap();
          /* eslint-disable new-cap */
          const firstBlock = blockMap.first().set('type', 'unstyled').set('data', Map());
          blockMap = blockMap.merge([[firstBlock.getKey(), firstBlock]]);
          break;
        }
        case sameTable: {
          // clear cell contents in part of a table
          blocks = blocks.butLast();
          blockMap = blockMap.merge(blocks);
          let subSelection = SelectionState.createEmpty(endKey);
          subSelection = subSelection.merge({
            focusOffset: selection.getEndOffset(),
          });
          content = content.set('blockMap', blockMap);
          content = Modifier.removeRange(content, subSelection, 'backward');
          blockMap = content.getBlockMap();
          break;
        }
        case firstCell: {
          // remove selected blocks, but just clear contents of blocks matching lastTableKey
          const notLastTable = blocks.filter(block => block.getData().get('tableKey') !== lastTableKey);
          blockMap = blockMap.merge(blocks.butLast());
          blockMap = blockMap.filter((_, key) => !notLastTable.has(key));
          let subSelection = SelectionState.createEmpty(endKey);
          subSelection = subSelection.set('focusOffset', selection.getEndOffset()) as SelectionState;
          content = content.set('blockMap', blockMap);
          content = Modifier.removeRange(content, subSelection, 'backward');
          blockMap = content.getBlockMap();
          break;
        }
        case lastCell: {
          // clear contents of blocks matching firstTableKey & remove all other selected blocks
          const notFirstTable = blocks.filter(block => block.getData().get('tableKey') !== firstTableKey);
          blockMap = blockMap.merge(blocks);
          blockMap = blockMap.filter((_, key) => !notFirstTable.has(key));
          break;
        }
        case startBlock.getType() === 'table' && endBlock.getType() === 'table' && !sameTable: {
          // clear contents of firstTableKey & lastTableKey, & delete all blocks in between, but leave one empty block to separate the tables.
          const notTable = blocks.filter(block => !block.getData().get('tableKey'));
          const separatorBlock = notTable.first();
          blockMap = blockMap.merge(blocks.butLast());
          blockMap = blockMap.filter((_, key) => !notTable.has(key) || key === separatorBlock.getKey());
          blockMap = blockMap.merge([[separatorBlock.getKey(), separatorBlock.set('text', ' ')]]);
          let subSelection = SelectionState.createEmpty(endKey);
          subSelection = subSelection.set('focusOffset', selection.getEndOffset()) as SelectionState;
          content = content.set('blockMap', blockMap);
          content = Modifier.removeRange(content, subSelection, 'backward');
          blockMap = content.getBlockMap();
          break;
        }
        case startBlock.getType() !== 'table' && endBlock.getType() === 'table': {
          //
          if (prevBlock?.getType() === 'table') {
            const separatorBlock = blocks.first().set('text', ' ');
            blocks = blocks.merge([[separatorBlock.getKey(), separatorBlock]]);
          }
          blockMap = blockMap.merge(blocks.butLast());
          blockMap = blockMap.filter(block => block.getLength());
          let subSelection = SelectionState.createEmpty(endKey);
          subSelection = subSelection.set('focusOffset', selection.getEndOffset()) as SelectionState;
          content = content.set('blockMap', blockMap);
          content = Modifier.removeRange(content, subSelection, 'backward');
          blockMap = content.getBlockMap();
          break;
        }
        default: {
          // clear contents of firstTableKey, delete other blocks
          const notTableStart = blocks.find(block => !block.getData().get('tableKey')).getKey();
          const table = blocks.filter(block => block.getData().get('tableKey') === firstTableKey);
          blockMap = blockMap.merge(table);
          content = content.set('blockMap', blockMap);
          let subSelection = SelectionState.createEmpty(notTableStart);
          subSelection = subSelection.merge({
            focusKey: endKey,
            focusOffset: endOffset,
          });
          content = Modifier.removeRange(content, subSelection, 'backward');
          blockMap = content.getBlockMap();
        }
      }
      // create a new collapsed selection positioned where the former selection started
      const selectionKey = blockMap.has(startKey) ?
        startKey :
        blockMap.has(endKey) ?
          endKey :
          prevBlock?.getKey() || nextBlock?.getKey() || blockMap.first().getKey();
      selection = SelectionState.createEmpty(selectionKey);
      selection = selection.merge({
        anchorKey: selectionKey,
        anchorOffset: startOffset + chars.length,
        focusKey: selectionKey,
        focusOffset: startOffset + chars.length,
      });
      content = content.set('blockMap', blockMap);
      newEditorState = EditorState.push(newEditorState, content, 'remove-range');
      newEditorState = EditorState.forceSelection(newEditorState, selection);
      onChange(newEditorState);
      return 'handled';
    };

    const handlePastedText = (text, pastedHtml, editorState) => {
    // when pasting into a table cell only allow plain text
      // to be inserted or the table will become corrupted
      let content = editorState.getCurrentContent();
      const selection = editorState.getSelection();
      const block = content.getBlockForKey(selection.getStartKey());
      if (block.getType() === 'table') {
        if (selection.isCollapsed()) {
          content = Modifier.insertText(content, selection, text);
        } else {
          content = Modifier.replaceText(content, selection, text);
        }
        onChange(EditorState.push(editorState, content, 'insert-characters'));
        return true;
      }
      if (pastedHtml) {
        return addHtmlToDocument(pastedHtml, editorState);
      }
      return false;
    };

    const reset = newHtml => {
      if (newHtml === html.current) return;

      let newEditorState = convertHtmlToEditorState(newHtml);
      if (newHtml) {
        newEditorState = EditorState.moveFocusToEnd(newEditorState);
      } else if (newHtml === null) {
        newEditorState = moveSelectionToStart(newEditorState);
      }
      onChange(newEditorState);
      // Resetting forces the editor to take focus, so here we check if the editorState
      // had focus before the reset() and if not then remove focus again.
      // Also keep the contenteditable state in sync with because
      // reset() leaves all contenteditable === true.
      const hasFocus = getCurrentEditorState.getSelection().getHasFocus();
      const currentEditor = editor.current;
      setTimeout(() => {
        currentEditor?.blur();
        setTimeout(() => {
          if (hasFocus) {
            currentEditor?.focus();
          }
        });
      });
    };


    const moveSelectionToStart = currentEditorState => {
      if (!currentEditorState || typeof currentEditorState.getSelection !== 'function') {
        return EditorState.createEmpty();
      }

      let selection = currentEditorState.getSelection();
      const content = currentEditorState.getCurrentContent();
      const firstBlock = content.getFirstBlock();
      const key = firstBlock.getKey();
      selection = SelectionState.createEmpty(key);
      return EditorState.forceSelection(currentEditorState, selection);
    };


    const blockRendererFn = myBlockRenderer(editor.current, getEditorState, onChange);
    const writeWithAi = () => {
      setIsOpenAiWriterDrawer(true);
      setAiWriter({
        showForm: true,
        templateUsed: 'new-ai-writer',
        templateIcon: 'hand-write-icon-color.svg',
        templateName: 'Complete SEO Article',
        templateDesc: 'Generate a full article from just a keyword (topic) or customize the article based on your ranking goals.',
        isStandaloneTool: false,
      });
      setAiTemplate({
        ...toJS(aiTemplatesController ?? []),
        isUseMask: true,
      });
    };

    // Add this helper function to ensure we always have a valid EditorState
    const ensureValidEditorState = (editorState: any): EditorState => {
      if (!editorState || typeof editorState.isInCompositionMode !== 'function') {
        return EditorState.createEmpty();
      }
      return editorState;
    };

    return (
      <>
        {newPage && <Welcome store={currentPage} />}
        {/* when calculating height we are taking in fixed height of top page header and toolbox and page-status and add dynamic target keywords container height */}
        <div
          id='editorContainerId'
          ref={editorContainer}
          onScroll={() => {
            // This is done to hide top bar if we scroll the editor enough
            const scrollTop = editorContainer.current.scrollTop;
            setLastScrollPosition(scrollTop);
            if (scrollTop >= offsetBreakPoint && !isEditorScrolled) {
              setIsEditorScrolled(true);
            } else if (scrollTop < 50 && isEditorScrolled) {
              setIsEditorScrolled(false);
            }
          }}
          className={classNames(styles.editor, currentPage?.isExportingPage && styles.expandedEditor, getZoomLevel())}
          style={{height: `calc(100vh - (${currentPage?.editorHeightOffset.breadcrumbHeight || 0}px + ${currentPage?.editorHeightOffset.keywordsHeight || 0}px - ${windowHeight > 800 ? 30 : 50}px + ${isEditorScrolled ? 47 : 111}px))`}}>

          <div style={{
            height: 'auto',
            background: '#fff',
            // marginBottom: '-10px',
            // marginLeft: '42px',
            // marginRight: '42px',
            padding: '20px 25px 0 25px',
            width: ' 816px',
            marginTop: '0px',
            display: showAiBanner && !aiInProgress ? 'block' : 'none',
          }}>
            <div style={{
              background: opaqueColor('#7F4EAD', 10),
              padding: '18px 36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderRadius: 15,
              border: '1px solid rgba(127, 78, 173, 0.30)',
              boxShadow: '0px 3px 5px 0px rgba(0, 0, 0, 0.08)'}}>
              <div>
                <FontAwesomeIcon icon={faBrainCircuit} fontSize={40} style={{marginRight: 20, color: '#7F4EAD', cursor: 'default'}}/></div>
              <div style={{textAlign: 'left', marginRight: 'auto'}}>
                <div style={{fontSize: 16, fontWeight: 500, color: '#121212', cursor: 'default'}}>Try out the most Powerful SEO AI Writer</div>
                <div style={{color: '#4E5156', cursor: 'default'}}>Generate a full article from a keyword or topic in just a few clicks.</div>
              </div>
              <div>
                <Button style={{boxShadow: '0px 3px 8px 0px rgba(0, 0, 0, 0.25)', marginLeft: 'auto'}} color='purple' onClick={writeWithAi}>
                Write with AI
                  <FontAwesomeIcon icon={faArrowRight} style={{marginLeft: 10}}/>
                </Button></div>
            </div>
          </div>
          <HeroImageWrapper style={{height: 'auto', background: '#fff', padding: '20px 25px 0 25px', width: ' 816px', marginTop: '0px'}}>
            {isHeroImageLoading || addHeroImageLoading ?
              <SkeletonRingLoader />:
              heroImage ?
                <div ref={heroSection}>
                  <HeroSection>
                    <HeroImage src={heroImage} alt={heroImageAltText} />
                    <div className='overlay'></div>
                    <ButtonsContainer className='button'>
                      <Tooltip title='Generate another image' getPopupContainer={trigger => trigger}>
                        <ButtonWrapper onClick={handleAddHeroImage}>
                          <FontAwesomeIcon icon={faArrowsRotate} />
                        </ButtonWrapper>
                      </Tooltip>
                      <Tooltip title='Provide a custom input' getPopupContainer={trigger => trigger}>
                        <ButtonWrapper onClick={addHeroImage}>
                          <FontAwesomeIcon icon={faICursor} />
                        </ButtonWrapper>
                      </Tooltip>
                      <Tooltip title='Remove hero image' getPopupContainer={trigger => trigger}>
                        <ButtonWrapper onClick={removeHeroImage}>
                          <FontAwesomeIcon icon={faTrash} />
                        </ButtonWrapper>
                      </Tooltip>
                    </ButtonsContainer>
                  </HeroSection>
                  {/* <HeroImage src={heroImage} alt={heroImageAltText}/> */}
                  {/* <FontAwesomeIcon icon={faCircleXmark} className='cross-icon' onClick={removeHeroImage} /> */}
                </div>:
                <div
                  onClick={handleAddHeroImage}
                  style={{padding: '18px 36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 15, border: '2px dashed #D8D8D8', boxShadow: '0px 3px 5px 0px rgba(0, 0, 0, 0.040)', cursor: 'pointer'}}>
                  <div>
                    <NextImg src={'/img/add-image-icon.svg'} />
                  </div>
                  <div style={{marginLeft: '4px'}}>
                    <div style={{color: '#8F8F8F'}}>
                      Click here to AI Generate a Hero Image
                    </div>
                  </div>
                </div>
            }
          </HeroImageWrapper>
          <div style={{height: 'auto', background: '#fff', padding: '20px 25px 0 25px', width: ' 816px', marginTop: '0px'}}>
          </div>
          {aiInProgress ?
            <AiBanner>
              <div className='container'>
                <div>
                  <Spin indicator={antIconOrange} />
                  {aiInProgress ? `Generated AI Content will be added to this article soon ${aiOutline?.shouldPopulatePageHtml ?' (overwriting any existing content)': ''}.` : ''}

                </div>
              </div>
            </AiBanner> :
            null}
          {/* {!showAiBanner && !currentPage.isTaActive && isLinkgraph?
            <div style={{
              position: 'absolute',
              right: isSideBarHidden ? 50 : 425,
              bottom: 150,
              zIndex: 9,
              backgroundColor: '#7F4EAD',
              boxShadow: '0px 3px 8px rgba(0, 0, 0, 0.15)',
              borderRadius: 12,
              padding: '10px 16px',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }} onClick={() =>setIsOpenAiWriterDrawer(true)}>
              <FontAwesomeIcon icon={faLightbulb} color={opaqueColor('#FFFFFF', 40)} fontSize={28}/>
              <div style={{textAlign: 'left', marginLeft: 10}}>
                <div style={{color: '#fff', fontWeight: 600, lineHeight: '16px'}}>{`New AI writer (alpha)`}</div>
              </div>
            </div> : null} */}
          {/* {!showAiBanner && !currentPage.isTaActive ?
            <div style={{
              position: 'absolute',
              right: isSideBarHidden ? 50 : 425,
              bottom: 50,
              zIndex: 9,
              backgroundColor: '#7F4EAD',
              boxShadow: '0px 3px 8px rgba(0, 0, 0, 0.15)',
              borderRadius: 12,
              padding: '10px 16px',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }} onClick={() =>setIsOpenAiContentToolsDrawer(true)}>
              <FontAwesomeIcon icon={faLightbulb} color={opaqueColor('#FFFFFF', 40)} fontSize={28}/>
              <div style={{textAlign: 'left', marginLeft: 10}}>
                <div style={{color: '#fff', fontWeight: 600, lineHeight: '16px'}}>Need help writing?</div>
                <div style={{color: '#fff'}}>Try AI Content Writer</div>
              </div>
            </div> : null} */}


          {!newPage && <>
            <Editor
              editorKey='editor'
              ref={editor}
              onChange={draftEditorState => {
                if (isInitialEditorStateFetched) {
                  const scrollY = editorContainer.current?.scrollTop;
                  const height = heroSection.current ? heroSection.current.offsetHeight : 250;
                  if (scrollY > height) {
                    setLastScrollPosition(scrollY);
                  }
                  onChange(draftEditorState);
                }
              }}
              editorState={ensureValidEditorState(getCurrentEditorState)}
              placeholder={aiInProgress ? '' : t('start-writing-own-content')}
              customStyleMap={styleMap}
              blockStyleFn={customBlockStyleFn}
              handleKeyCommand={handleKeyCommand}
              keyBindingFn={keyBindingFn}
              blockRendererFn={blockRendererFn}
              handlePastedText={(text, html, editorState) => handlePastedText(text, html, editorState) ? 'handled' : 'not-handled'}
            />
          </>
          }
        </div>
      </>
    );
  },
);

const HeroImageWrapper = styled.div`
  height: auto;
  background: #fff;
  padding: 20px 25px 0 25px;
  width: 816px;
  margin-Top: 0px;
  cursor: auto;

  .button {
    visibility: hidden;
    :hover {
    visibility: visible;
  }
  }

  :hover .button {
    visibility: visible;
  }
`;

const AiBanner = styled.div`
  height: auto;
  background: #fff;
  padding: 20px 25px 0 25px;
  width:  816px;
  margin-top: 0px;
  
  .container {
    background: rgb(255, 133, 54, 0.15);
    padding: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 15px;
  }
`;

const HeroSection = styled.div`
  width: 100%;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0);
    border-radius: 12px;
    transition: 0.3s;
  }

  &:hover::before {
    background-color: rgba(0, 0, 0, 0.2);
  }
`;

const HeroImage = styled.img`
  width: 100%;
  border-radius: 12px;
`;

const ButtonsContainer = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  display: flex;
  gap: 10px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgb(255, 255, 255, 0.4);
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
`;
